import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { PaginationResponse } from "@/core/services/api/interfaces/PaginationResponse";
import ApiService from "@/core/services/ApiService";
import { AxiosRequestConfig } from "axios";
import { Setting } from "@/core/services/api/interfaces/admin/Setting";
import { SettingKeys } from "@/core/services/api/enums/SettingEnums";

@Module
export default class SettingsModule extends VuexModule {
  pagination = new PaginationResponse<Setting>();
  loading = false;

  get settings(): Setting[] {
    return this.settingsPagination.data;
  }

  get settingsLoading(): boolean {
    return this.loading;
  }

  get settingsPagination(): PaginationResponse<Setting> {
    return this.pagination;
  }

  get settingsKeyValue(): { [key: string]: string } {
    return this.settings.reduce((acc, setting) => {
      acc[setting.key] = setting.value;

      return acc;
    }, {});
  }

  get settingsFlatten(): { [key: string]: string } {
    const settings = this.settingsKeyValue;
    const fullSettings = {};
    for (const key of Object.keys(SettingKeys)) {
      fullSettings[key] = settings[key] || "";
    }

    return fullSettings;
  }

  get settingsTotal(): number {
    return this.pagination.total;
  }

  get settingsRequired(): boolean {
    return !this.pagination.total && !this.loading;
  }

  get settingsCurrentPage(): number {
    return this.pagination.current_page;
  }

  get integritiesPerPage(): number {
    return this.pagination.per_page;
  }

  @Action({ rawError: true })
  async [Actions.ADMIN_SETTINGS_LOAD](params) {
    this.context.commit(Mutations.ADMIN_SETTINGS_SET_LOADING, true);
    return ApiService.admin.settings
      .list({
        params: params,
      } as AxiosRequestConfig)
      .then(ApiService.unwrapPaginationResponse<Setting>)
      .then((response) => {
        this.context.commit(Mutations.ADMIN_SETTINGS_SET, response);
      })
      .catch(console.error)
      .finally(() =>
        this.context.commit(Mutations.ADMIN_SETTINGS_SET_LOADING, false)
      );
  }

  @Action({ rawError: true })
  async [Actions.ADMIN_SETTINGS_UPSERT]({ key, value }) {
    this.context.commit(Mutations.ADMIN_SETTINGS_SET_LOADING, true);
    return ApiService.admin.settings
      .upsert({
        key,
        value,
      } as AxiosRequestConfig)
      .then(ApiService.unwrapPaginationResponse<Setting>)
      .then((response) => {
        this.context.commit(Mutations.ADMIN_SETTINGS_SET, response);
      })
      .finally(() =>
        this.context.commit(Mutations.ADMIN_SETTINGS_SET_LOADING, false)
      );
  }

  @Mutation
  [Mutations.ADMIN_SETTINGS_SET](pagination: PaginationResponse<Setting>) {
    this.pagination = pagination;
  }

  @Mutation
  [Mutations.ADMIN_SETTINGS_SET_LOADING](loading: boolean) {
    this.loading = loading;
  }
}
